import React, { useState, useEffect, useMemo } from "react";
import { Timestamp } from "firebase/firestore";
import {
  Button,
  Box,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { CBox } from "components";
import RevenueTable from "../RevenueTable";
import ValidationSummaryReportTable from "../RevenueTable/ValidationSummaryReportTable";
import DailyValidationTable from "../RevenueTable/DailyValidationTable";

import DailyReportTable from "../DailyReportTable/DailyReportTable";
import { EntryService } from "services/Entry.Services";
import { GroupedLocation, Location } from "models/schema";
import { useDataContext } from "context/DataContext";
import moment from "moment";
import {
  getShiftsWithDepartments,
  logWorkforceDailyHours,
  shiftsMatchingLocationRc,
  clearWorkforceShiftsCache,
  WORKFORCE_ENABLED,
} from "services/workforce-api";
import { revenueReportYmdRange } from "utils/revenueReportDates";

/** Summary report aggregates Firestore daily stats; when none exist for the range, still show rows so Workforce hours render. */
function summaryFallbackRowsFromSelections(
  groupedLocations: GroupedLocation[],
  selectedNames: string[]
): Array<{
  locationId: string;
  location: Location;
  stand: string;
  totalCars: number;
  totalRevenue: number;
  totalValidationCars: number;
  totalValidationRevenue: number;
  totalCreditCard: number;
  totalConditinalTips: number;
  totalTips: number;
  totalCcTipsPaid: number;
  totalCreditCardSurcharge: number;
  totalblubar: number;
  totalcalculatedCashTurnIn: number;
  totalDepositAmount: number;
  entriesCount: number;
}> {
  const now = Timestamp.now();
  const minimalLocation = (g: GroupedLocation): Location => ({
    id: `fallback-${g.locationRc}`,
    locationRc: g.locationRc,
    locationName: g.locationName,
    standNumber: "",
    standName: "",
    blueBar: "",
    rates: [],
    allRates: [],
    conditionalVip: "",
    conditionalRates: [],
    validations: [],
    users: [],
    stands: [],
    createdAt: now,
    updatedAt: now,
  });

  return groupedLocations
    .filter((g) => selectedNames.includes(g.locationName))
    .map((g) => {
      const stand = g.stands[0];
      const location = stand?.location ?? minimalLocation(g);
      return {
        locationId: stand?.id ?? `fallback-${g.locationRc}`,
        location,
        stand: "",
        totalCars: 0,
        totalRevenue: 0,
        totalValidationCars: 0,
        totalValidationRevenue: 0,
        totalCreditCard: 0,
        totalConditinalTips: 0,
        totalTips: 0,
        totalCcTipsPaid: 0,
        totalCreditCardSurcharge: 0,
        totalblubar: 0,
        totalcalculatedCashTurnIn: 0,
        totalDepositAmount: 0,
        entriesCount: 0,
      };
    });
}

const RevenueData = ({ locations }: { locations: GroupedLocation[] }) => {
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedLocationNames, setSelectedLocationNames] = useState<string[]>(
    []
  );
  const [report, setReport] = useState<"Daily" | "Summary">("Daily");
  const [stands, setStands] = useState("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [isValidationReport, setIsValidationReport] = useState(false);
  const [triggerExport, setTriggerExport] = useState(false);

  const { dates } = useDataContext();
  const fetchIdRef = React.useRef(0);

  const reportYmd = useMemo(
    () => revenueReportYmdRange(dates.startDate, dates.endDate),
    [dates.startDate, dates.endDate]
  );

  // Stand ID → Location object. Passed to getRevenueReport so it skips
  // the sequential locations Firestore round-trip.
  const locationMap = useMemo(() => {
    const map: Record<string, any> = {};
    locations.forEach((group) =>
      group.stands.forEach((stand) => { map[stand.id] = stand.location; })
    );
    return map;
  }, [locations]);
  const handleLocationChange = (event: any) => {
    const {
      target: { value },
    } = event;

    const selectedLocationNames =
      typeof value === "string" ? value.split(",") : value;

    const matchingLocations = locations.filter((location) =>
      selectedLocationNames.includes(location.locationName)
    );

    const selectedIds = matchingLocations.flatMap((location) =>
      location.stands.map((stand) => stand.id)
    );

    setSelectedLocationIds(selectedIds);
    setSelectedLocationNames(selectedLocationNames);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allNames = locations.map((location) => location.locationName);
      const allIds = locations.flatMap((location) =>
        location.stands.map((stand) => stand.id)
      );

      setSelectedLocationIds(allIds);
      setSelectedLocationNames(allNames);
    } else {
      setSelectedLocationIds([]);
      setSelectedLocationNames([]);
    }
  };

  const handleClearSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedLocationIds([]);
    setSelectedLocationNames([]);
  };

  const handleReportChange = (event: any) => {
    setReport(event.target.value);
  };

  const handleExportClick = () => {
    setTriggerExport(true);
  };

  const onExportComplete = () => {
    setTriggerExport(false);
  };

  const handleStandsChange = (event: any) => {
    setStands(event.target.value);
  };
  const handleValidationClick = () => {
    setIsValidationReport(!isValidationReport);
  };

  const fetchReportData = async () => {
    // Track this fetch so a later fetch can cancel a stale workforce update.
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      if (
        WORKFORCE_ENABLED &&
        process.env.REACT_APP_WORKFORCE_CLEAR_CACHE_EACH_FETCH === "true"
      ) {
        clearWorkforceShiftsCache();
      }
      const startDate = dates.startDate;
      const endDate = dates.endDate;
      const fromStr = moment(startDate).format("YYYY-MM-DD");
      const toStr = moment(endDate).format("YYYY-MM-DD");

      // Start workforce fetch immediately so it runs in parallel,
      // but don't await it yet — we show revenue data as soon as Firestore responds.
      // Skipped entirely when the workforce integration is disabled for this env.
      const workforcePromise = WORKFORCE_ENABLED
        ? getShiftsWithDepartments(fromStr, toStr, false)
        : null;
      if (workforcePromise) setHoursLoading(true);

      const data = await EntryService.getRevenueReport(
        selectedLocationIds,
        startDate,
        endDate,
        report,
        isValidationReport,
        locationMap
      );

      // Abort if a newer fetch has already started.
      if (fetchId !== fetchIdRef.current) return;

      const baseReportRows =
        report === "Summary" &&
        !isValidationReport &&
        Array.isArray(data) &&
        data.length === 0 &&
        selectedLocationNames.length > 0
          ? summaryFallbackRowsFromSelections(locations, selectedLocationNames)
          : data;

      // Show revenue data immediately — hours column will fill in below (if enabled).
      setTableData(baseReportRows.map((item) => ({ ...item, shifts: [] })));
      setLoading(false);

      // No workforce integration: revenue data is the final result.
      if (!workforcePromise) return;

      // Wait for workforce data and update hours in the background.
      const workforceData = await workforcePromise;

      if (fetchId !== fetchIdRef.current) return;

      const dataWithHours = baseReportRows.map((item) => ({
        ...item,
        shifts: shiftsMatchingLocationRc(workforceData, item.location.locationRc),
      }));

      if (!isValidationReport && report === "Daily") {
        logWorkforceDailyHours({
          range: revenueReportYmdRange(startDate, endDate),
          selectedLabel:
            selectedLocationNames.length > 0
              ? selectedLocationNames.join(", ")
              : "(no locations)",
          entries: dataWithHours.map((item) => ({
            locationName: item.location?.locationName ?? "",
            locationRc: String(item.location?.locationRc ?? ""),
            shifts: item.shifts ?? [],
          })),
        });
      }

      setTableData(dataWithHours);
      setHoursLoading(false);
    } catch (error) {
      if (fetchId !== fetchIdRef.current) return;
      setTableData([]);
      setHoursLoading(false);
      console.error("Error fetching report data:", error);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedLocationIds.length > 0) {
      fetchReportData();
    } else {
      setTableData([]);
    }
  }, [selectedLocationIds, report, dates, isValidationReport]);
  return (
    <React.Fragment>
      <Box>
        <CBox
          jsb
          as
          wrp
          px={{ xs: 0, md: 4 }}
          display={"flex"}
          alignItems={"center"}
          gap={2}>
          {/* Dropdown for Select Location */}
          <Select
            multiple
            displayEmpty
            value={selectedLocationNames}
            onChange={handleLocationChange}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <span>Select Location</span>;
              }
              if (selected.length > 1) {
                return (
                  <Box display="flex" alignItems="center">
                    <span>Locations Selected</span>
                    <Box
                      display="flex"
                      gap={"3px"}
                      alignItems="center"
                      bgcolor={"#0A9959"}
                      borderRadius={"5px"}>
                      <Box
                        ml={1}
                        px={"3px"}
                        py={0.5}
                        color="white"
                        borderRadius={1}>
                        {selected.length}
                      </Box>
                      <IconButton onClick={handleClearSelection} size="small">
                        <img src="/assetsOaks/icon/cancel.svg" alt="Cancel " />
                      </IconButton>
                    </Box>
                  </Box>
                );
              }
              return selected.join(", "); // Show selected names
            }}
            sx={{
              borderRadius: "7px",
              height: "51px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Default border color
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Border color on hover
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Border color when focused
              },
            }}>
            <MenuItem value="all">
              <Checkbox
                checked={selectedLocationNames.length === locations.length}
                onChange={handleSelectAll}
              />
              <ListItemText primary="Select All" />
            </MenuItem>
            {locations.map((location) => (
              <MenuItem
                key={location.locationName}
                value={location.locationName}>
                <Checkbox
                  checked={selectedLocationNames.includes(
                    location.locationName
                  )}
                />
                <ListItemText
                  primary={`${location.locationRc} - ${location.locationName}`}
                />
              </MenuItem>
            ))}
          </Select>

          {/* Dropdown for Summary Report */}
          <Select
            value={report}
            onChange={handleReportChange}
            displayEmpty
            sx={{
              minWidth: 227,
              borderRadius: "7px",
              height: "51px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Default border color
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Border color on hover
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Border color when focused
              },
            }}>
            <MenuItem value="Daily">Daily Report</MenuItem>
            <MenuItem value="Summary">Summary Report</MenuItem>
          </Select>
          <Select
            value={stands}
            onChange={handleStandsChange}
            displayEmpty
            sx={{
              minWidth: 227,
              borderRadius: "7px",
              height: "51px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Default border color
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Border color on hover
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "black", // Border color when focused
              },
            }}>
            <MenuItem value="">
              <span>Show Stands</span>
            </MenuItem>
            <MenuItem value="Stand 1">Hide Stands</MenuItem>
          </Select>

          {/* Buttons */}
          <Button
            variant="contained"
            onClick={handleValidationClick}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#0A9959",
              height: "40px",
              color: "#fff",
              marginLeft: "auto", // This pushes the buttons to the right
            }}>
            <img src="/assetsOaks/icon/right.svg" alt="plus" />
            <span style={{ marginLeft: "10px" }}>
              {isValidationReport ? "Stop Validation" : "Run Validation"}
            </span>
          </Button>

          <Button
            variant="contained"
            onClick={handleExportClick}
            sx={{
              display: "flex",
              height: "40px",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#0A9959",
              color: "#fff",
            }}>
            <Box
              component="img"
              src="/assetsOaks/icon/dowload.svg"
              alt="export"
              sx={{ width: "19px", height: "19px", objectFit: "contain" }}
            />
            Export
          </Button>
        </CBox>
      </Box>

      {hoursLoading && !loading && (
        <Box display="flex" alignItems="center" gap={1} px={{ xs: 0, md: 4 }} mt={1} mb={-1}>
          <CircularProgress size={14} />
          <Box component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
            Loading hours…
          </Box>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : isValidationReport ? (
        report === "Daily" ? (
          <DailyValidationTable
            validationData={tableData}
            stands={stands}
            triggerExport={triggerExport}
            onExportComplete={onExportComplete}
          />
        ) : (
          <ValidationSummaryReportTable
            validationData={tableData}
            stands={stands}
            triggerExport={triggerExport}
            onExportComplete={onExportComplete}
          />
        )
      ) : report === "Daily" ? (
        <DailyReportTable
          tableData={tableData}
          stands={stands}
          triggerExport={triggerExport}
          onExportComplete={onExportComplete}
          reportStartYmd={reportYmd.startYmd}
          reportEndYmd={reportYmd.endYmd}
        />
      ) : (
        <RevenueTable
          tableData={tableData}
          stands={stands}
          triggerExport={triggerExport}
          onExportComplete={onExportComplete}
          startDate={dates.startDate}
          endDate={dates.endDate}
        />
      )}
    </React.Fragment>
  );
};

export default RevenueData;
